import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { Skeleton, TextField } from "@mui/material";
import { DateWithTooltip } from "components/DateWithTooltip";

import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useDownDerateTrackerData } from "contexts/DownDerateDataContext";
import { useFetchPatchV2 } from "hooks/fetch/useFetchPatchV2";
import { dateFormat } from "core/datetime";
import { styled } from "@mui/material/styles";

export function RTSDatePicker({ cell, rowOriginal }) {
  const [isDataStale, setIsDataStale] = useState(false);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { refetchDownDerateTableData } = useDownDerateTrackerData();

  const machineName = rowOriginal?.machine_name;
  const isActualRTS = rowOriginal?.actual_rts;
  const isOffLineAndDerated =
    rowOriginal?.machine_status_name === "Offline" ||
    rowOriginal?.machine_status_name === "Derated";

  const currentRow = cell.row.original;
  const downDate = new Date(currentRow.down_date);
  const rtsId = `rts${currentRow.id}`;

  const initDate = date => {
    return date ? new Date(date) : "";
  };

  const toDateTimePickerFormat = date => {
    if (!date) {
      return "";
    }

    try {
      let dateStr = date.toLocaleString("sv").replace(" ", "T");
      return dateStr.slice(0, -3);
    } catch (error) {
      return "";
    }
  };

  const originalRts = toDateTimePickerFormat(
    initDate(rowOriginal?.expected_rts)
  );
  let [currentRts, setCurentRts] = useState(
    toDateTimePickerFormat(initDate(rowOriginal?.expected_rts))
  );

  // We need to update the value in the picker when it changes externally
  useEffect(() => {
    setCurentRts(toDateTimePickerFormat(initDate(rowOriginal?.expected_rts)));
  }, [rowOriginal]);

  useEffect(() => {
    setIsDataStale(false);
  }, [cell, rowOriginal]);

  const {
    loading: createMachineStatusLoading,
    patchCallback: patchMachineStatus
  } = useFetchPatchV2(`machine_status/${cell.row.original.machine_status_id}`);

  const updateRTS = newValue => {
    let rtsDate;
    if (newValue) {
      rtsDate = new Date(newValue);
      rtsDate.setSeconds(0, 0);
    } else {
      rtsDate = null;
    }

    if (rtsDate && rtsDate < downDate) {
      const dateFormatOptions = {
        format: "Do MMM YYYY h:mma"
      };

      const displayRtsDate = dateFormat(rtsDate, dateFormatOptions);
      const displayDownDate = dateFormat(downDate, dateFormatOptions);

      addSnackbarToQueue({
        message: `Invalid date entered (${displayRtsDate}). Expected RTS date must be after the date set (${displayDownDate}).`,
        severity: "error"
      });
      setCurentRts(originalRts);
      return false;
    } else {
      setIsDataStale(true);
    }

    patchMachineStatus({ expected_rts: rtsDate ? rtsDate : null }).then(
      _success => {
        addSnackbarToQueue({
          message: `Expected RTS for ${machineName} has been updated.`,
          severity: "success"
        });
        refetchDownDerateTableData();
      },
      _errorResponse => {
        addSnackbarToQueue({
          message: "Expected RTS failed to update.",
          severity: "error"
        });
      }
    );
  };

  if (createMachineStatusLoading || isDataStale) return <Skeleton />;

  const blurRTS = () => {
    const rtsElem = document.getElementById(rtsId);
    rtsElem.blur();
  };

  return isActualRTS || !isOffLineAndDerated ? (
    <DateWithTooltip date={currentRts} />
  ) : (
    <StyledTextField
      id={rtsId}
      onBlur={e => {
        if ((originalRts || "") !== (currentRts || "")) {
          updateRTS(currentRts);
        }
      }}
      size="small"
      type="datetime-local"
      name="expected_rts"
      value={currentRts}
      onChange={e => {
        console.log(
          "originalRts: " + originalRts + ", HTML: " + e.target.value
        );
        setCurentRts(e.target.value);

        if (!e.target.value) {
          // User cleared it
          setTimeout(blurRTS, 100);
        }
      }}
      variant="outlined"
      margin="dense"
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        min: toDateTimePickerFormat(initDate(downDate)),
        onKeyDown: e => {
          e.preventDefault();
        }
      }}
    />
  );
}

RTSDatePicker.propTypes = {
  cell: PropTypes.object,
  rowOriginal: PropTypes.object
};

const StyledTextField = styled(TextField)`
  .MuiInputBase-input {
    font-size: 14px;
  }
`;
